import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Lock, Copy, Check, Webhook, FlaskConical, Plus, X } from "lucide-react";
import { API_BASE, api } from "@/lib/api";

/**
 * A connector form rendered from the server's own description of the kind.
 *
 * The page it replaced hardcoded five IMAP fields. SMTP was not among them, so
 * every connector created through the UI could receive mail and silently never
 * reply — the outbound executor looks for `config.smtp_host` and found nothing.
 * Nothing errored; replies simply had no route out.
 *
 * Rendering from `GET /connectors/kinds` means there is one description of a
 * connector's settings rather than two that can drift apart, and adding a kind
 * needs no new form.
 */

const SecretField = ({ field, value, hasStored, onChange }) => {
  // A stored credential is write-only — the API returns whether one exists and
  // nothing more. Showing an empty box next to a connector that plainly works
  // reads as "this is missing", so say what is true and make replacing it
  // deliberate.
  const [replacing, setReplacing] = useState(!hasStored);

  if (hasStored && !replacing) {
    return (
      <div>
        <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
          {field.label}
        </label>
        <div className="flex items-center gap-2 mt-1">
          <span className="badge b-muted">
            <Lock size={9} className="inline mr-1" /> stored
          </span>
          <button type="button" className="btn btn-ghost"
                  style={{ padding: "3px 9px", fontSize: 11 }}
                  data-testid={`replace-${field.name}`}
                  onClick={() => setReplacing(true)}>
            Replace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
        {field.label}{field.required && " *"}
      </label>
      <input
        className="input mt-1"
        type="password"
        autoComplete="new-password"
        data-testid={`field-${field.name}`}
        value={value ?? ""}
        onChange={(e) => onChange(field.name, e.target.value)}
      />
      {field.help && (
        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{field.help}</p>
      )}
    </div>
  );
};

// Our case statuses, in workflow order, for the status map.
const CASE_STATUSES = ["new", "triaged", "in_progress", "waiting_internal",
                       "waiting_customer", "resolved", "closed"];

/**
 * A name-to-value map, edited as rows.
 *
 * It rendered as a text box before, which showed "[object Object]" for a saved
 * map and saved whatever was typed as a string, which the adapters then could
 * not read. The rows live here so a half-typed row (a name, no value yet) is
 * not dropped mid-edit; only complete rows are reported.
 */
const KvField = ({ field, value, onChange }) => {
  const [rows, setRows] = useState(() => {
    const entries = Object.entries(value && typeof value === "object" ? value : {});
    return entries.length ? entries : [["", ""]];
  });

  const update = (next) => {
    setRows(next);
    onChange(field.name, Object.fromEntries(
      next.filter(([k, v]) => k.trim() && String(v).trim())
    ));
  };
  const setRow = (i, col, text) =>
    update(rows.map((r, j) => (j === i ? (col === 0 ? [text, r[1]] : [r[0], text]) : r)));

  return (
    <div>
      <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
        {field.label}{field.required && " *"}
      </label>
      {rows.map(([k, v], i) => (
        <div key={i} className="flex gap-2 mt-1 items-center">
          <input className="input" placeholder="name" value={k}
                 data-testid={`field-${field.name}-key-${i}`}
                 onChange={(e) => setRow(i, 0, e.target.value)} />
          <span style={{ color: "var(--text-muted)" }}>→</span>
          <input className="input" placeholder="value" value={v}
                 data-testid={`field-${field.name}-value-${i}`}
                 onChange={(e) => setRow(i, 1, e.target.value)} />
          <button type="button" className="btn btn-ghost" style={{ padding: "3px 6px" }}
                  aria-label="Remove row"
                  onClick={() => update(rows.length > 1 ? rows.filter((_, j) => j !== i) : [["", ""]])}>
            <X size={11} />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost mt-1" style={{ padding: "3px 9px", fontSize: 11 }}
              data-testid={`field-${field.name}-add`}
              onClick={() => setRows([...rows, ["", ""]])}>
        <Plus size={11} /> Add
      </button>
      {field.help && (
        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{field.help}</p>
      )}
    </div>
  );
};

const Field = ({ field, value, onChange }) => {
  if (field.type === "kv") return <KvField field={field} value={value} onChange={onChange} />;

  const common = {
    className: "input mt-1",
    "data-testid": `field-${field.name}`,
    value: value ?? "",
    onChange: (e) => onChange(field.name, e.target.value),
  };

  if (field.type === "bool") {
    return (
      <label className="flex items-center gap-2 text-[12.5px] mt-5"
             style={{ color: "var(--text-secondary)" }}>
        <input type="checkbox" data-testid={`field-${field.name}`}
               checked={!!value} onChange={(e) => onChange(field.name, e.target.checked)} />
        {field.label}
      </label>
    );
  }

  return (
    <div>
      <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
        {field.label}{field.required && " *"}
      </label>
      {field.type === "select" ? (
        <select {...common}>
          {/* An optional select can be left to the server's rule for it,
              rather than showing its first option as though it were chosen. */}
          {!field.required && <option value="">Automatic</option>}
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          {...common}
          type={field.type === "number" ? "number" : "text"}
          // The server's default as a placeholder, never as a value: writing it
          // in would store it, freezing today's default into this connector for
          // good. An empty field means "whatever the current default is".
          placeholder={field.default != null ? String(field.default) : ""}
        />
      )}
      {field.help && (
        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{field.help}</p>
      )}
    </div>
  );
};

// Mirrors effective_auth_mode on the server: a CDP cannot sign, so it sends the
// secret as a bearer token unless told otherwise; your own backend signs.
const CDP_FORMATS = ["segment", "rudderstack", "mparticle", "posthog"];
export function analyticsAuthMode(config = {}) {
  if (config.auth_mode === "hmac" || config.auth_mode === "bearer") return config.auth_mode;
  return CDP_FORMATS.includes(config.format || "segment") ? "bearer" : "hmac";
}

export default function ConnectorForm({ kind, connector, onSaved, onCancel }) {
  const isEdit = !!connector;
  const [name, setName] = useState(connector?.name || "");
  const [config, setConfig] = useState(() => ({ ...(connector?.config || {}) }));
  const [secrets, setSecrets] = useState({});
  const [live, setLive] = useState(connector?.live ?? false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tested, setTested] = useState(null);
  const [secretIssued, setSecretIssued] = useState(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const [dryRun, setDryRun] = useState(connector?.writeback_dry_run ?? true);
  const writesBack = kind.capabilities?.includes("outbound_writeback");
  const [statusMap, setStatusMap] = useState(() => ({ ...(connector?.status_map || {}) }));
  const [writebackEnabled, setWritebackEnabled] = useState(connector?.writeback_enabled ?? false);

  const [basic, advanced] = useMemo(() => {
    const fields = kind.config_fields || [];
    return [fields.filter((f) => !f.advanced), fields.filter((f) => f.advanced)];
  }, [kind]);

  const setField = (n, v) => setConfig((c) => ({ ...c, [n]: v }));
  const setSecret = (n, v) => setSecrets((s) => ({ ...s, [n]: v }));

  const payload = () => {
    // Empty strings are absent, not blank values: an empty smtp_host must mean
    // "not configured" rather than a host named "".
    const cleaned = Object.fromEntries(
      Object.entries(config).filter(([, v]) => v !== "" && v != null)
    );
    const creds = Object.fromEntries(
      Object.entries(secrets).filter(([, v]) => v !== "" && v != null)
    );
    return {
      config: cleaned,
      credentials: Object.keys(creds).length ? creds : null,
      live,
      // Only kinds that write back take these; the server refuses them otherwise.
      ...(writesBack ? { status_map: statusMap, writeback_enabled: writebackEnabled } : {}),
    };
  };

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(""); setTested(null);
    try {
      const body = payload();
      const saved = isEdit
        ? (await api.patch(`/connectors/${connector.id}`, { name, ...body })).data
        : (await api.post("/connectors", { name, type: kind.kind, ...body })).data;
      // A changed status map goes back to rehearsing on the server.
      if (saved?.writeback_dry_run != null) setDryRun(saved.writeback_dry_run);
      onSaved?.(saved);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save this connector");
    } finally {
      setBusy(false);
    }
  };

  const rotateSecret = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/connectors/${connector.id}/rotate-webhook-secret`);
      setSecretIssued(data.secret);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not issue a signing secret");
    } finally {
      setBusy(false);
    }
  };

  const confirmWriteback = async () => {
    if (!window.confirm(
      "Start writing into the real ticket system?\n\n" +
      "Until now Prioris has only recorded what it would have done. From here " +
      "it changes statuses your customers can see. Check the rehearsals in " +
      "Settings \u2192 Audit first."
    )) return;
    setBusy(true); setError("");
    try {
      await api.post(`/connectors/${connector.id}/confirm-writeback`);
      setDryRun(false);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not confirm write-back");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true); setError(""); setTested(null);
    try {
      const { data } = await api.post(`/connectors/${connector.id}/test`);
      setTested(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not reach this connector");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card p-5" onSubmit={save} data-testid="connector-form">
      <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
        {kind.label}
      </div>
      <p className="text-[12px] mb-4" style={{ color: "var(--text-secondary)" }}>
        {kind.description}
      </p>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-6">
          <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>Name *</label>
          <input className="input mt-1" data-testid="field-name" value={name} required
                 placeholder="e.g. Support mailbox"
                 onChange={(e) => setName(e.target.value)} />
        </div>
        {basic.map((f) => (
          <div key={f.name} className={f.type === "kv" ? "col-span-12" : f.type === "number" || f.type === "bool" ? "col-span-12 md:col-span-3" : "col-span-12 md:col-span-6"}>
            <Field field={f} value={config[f.name]} onChange={setField} />
          </div>
        ))}
        {(kind.secret_fields || []).map((f) => (
          <div key={f.name} className="col-span-12 md:col-span-6">
            <SecretField field={f} value={secrets[f.name]}
                         hasStored={isEdit && connector.has_credentials}
                         onChange={setSecret} />
          </div>
        ))}
      </div>

      {advanced.length > 0 && (
        <div className="mt-4">
          <button type="button" className="btn btn-ghost"
                  style={{ padding: "3px 9px", fontSize: 11 }}
                  data-testid="toggle-advanced"
                  onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            Advanced
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-12 gap-3 mt-3">
              {advanced.map((f) => (
                <div key={f.name} className={f.type === "kv" ? "col-span-12" : f.type === "bool" ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-4"}>
                  <Field field={f} value={config[f.name]} onChange={setField} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Write-back is the only thing here that changes something a customer
          can see, so it says plainly which of the two states it is in. */}
      {writesBack && (
        <div className="mt-4 p-4 rounded-lg" data-testid="writeback-panel"
             style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
          <div className="text-[11px] uppercase tracking-wider mb-2"
               style={{ color: "var(--text-muted)" }}>
            <FlaskConical size={11} className="inline mr-1" /> Writing back
          </div>
          <label className="flex items-center gap-2 text-[12.5px]"
                 style={{ color: "var(--text-secondary)" }}>
            <input type="checkbox" checked={writebackEnabled} data-testid="field-writeback-enabled"
                   onChange={(e) => setWritebackEnabled(e.target.checked)} />
            Update tickets when their case changes status
          </label>
          <p className="text-[11px] mt-2 mb-1" style={{ color: "var(--text-muted)" }}>
            The status each case status should set in your ticket system, by the
            name you see there. The same names are read back when a ticket
            changes. Leave a row empty to never set it. Changing this starts a
            new rehearsal.
          </p>
          <div className="grid grid-cols-12 gap-2">
            {CASE_STATUSES.map((s) => (
              <React.Fragment key={s}>
                <div className="col-span-5 md:col-span-3 text-[12px] self-center font-mono"
                     style={{ color: "var(--text-secondary)" }}>{s}</div>
                <input className="input col-span-7 md:col-span-9" value={statusMap[s] ?? ""}
                       data-testid={`status-map-${s}`}
                       onChange={(e) => setStatusMap((m) => ({ ...m, [s]: e.target.value }))} />
              </React.Fragment>
            ))}
          </div>
          {!isEdit ? null : dryRun ? (
            <>
              <span className="badge b-wait mt-3 inline-block">rehearsing</span>
              <p className="text-[11.5px] mt-2" style={{ color: "var(--text-secondary)" }}>
                Prioris is recording what it <em>would</em> write into your tickets
                without sending any of it. Review those entries under
                Settings → Audit, then confirm.
              </p>
              <button type="button" className="btn btn-ghost mt-2" disabled={busy}
                      data-testid="confirm-writeback" onClick={confirmWriteback}>
                Start writing for real
              </button>
            </>
          ) : (
            <>
              <span className="badge b-healthy mt-3 inline-block">live</span>
              <p className="text-[11.5px] mt-2" style={{ color: "var(--text-secondary)" }}>
                Status changes here are written into your ticket system.
              </p>
            </>
          )}
        </div>
      )}

      {/* A webhook nobody can find the URL for is unusable, so the endpoint
          lives next to the settings rather than in documentation. The secret
          appears once, when it is issued. */}
      {isEdit && kind.capabilities?.includes("inbound_push") && (
        <div className="mt-4 p-4 rounded-lg" data-testid="webhook-panel"
             style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
          <div className="text-[11px] uppercase tracking-wider mb-2"
               style={{ color: "var(--text-muted)" }}>
            <Webhook size={11} className="inline mr-1" /> Your endpoint
          </div>
          <div className="font-mono text-[11.5px] break-all mb-2"
               data-testid="webhook-url">
            POST {API_BASE}/webhooks/{connector.id}
          </div>
          {kind.kind === "analytics_events" && analyticsAuthMode(connector.config) === "bearer" ? (
            // Segment and RudderStack destinations can send a fixed header but
            // cannot sign a body, so analytics connectors default to a bearer secret.
            <p className="text-[11.5px] mb-3" style={{ color: "var(--text-secondary)" }}
               data-testid="webhook-bearer-help">
              Add this URL as a destination in your CDP (Segment, RudderStack, mParticle, PostHog) with the header{" "}
              <span className="font-mono">Authorization: Bearer &lt;secret&gt;</span>. A backend using
              Segment's or RudderStack's server library sets its host to this URL and its write key to the secret.
              Requests from a browser or app are refused: never put the secret in client code.
            </p>
          ) : (
            <p className="text-[11.5px] mb-3" style={{ color: "var(--text-secondary)" }}>
              From your server, sign the raw request body with HMAC-SHA256 and send it as{" "}
              <span className="font-mono">X-Prioris-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</span>,
              where the signed value is <span className="font-mono">t + "." + body</span>.
              Requests from a browser or app are refused: keep the secret on your servers.
              {connector.config?.format === "ga4" && (
                <> For Google Analytics 4 / Firebase, the GA4 BigQuery push job signs for you: give it this URL and the secret.</>
              )}
            </p>
          )}
          {secretIssued ? (
            <div>
              <button type="button" className="btn btn-ghost font-mono text-[12px] break-all text-left"
                      data-testid="copy-webhook-secret"
                      onClick={() => { navigator.clipboard?.writeText(secretIssued);
                                       setSecretCopied(true);
                                       setTimeout(() => setSecretCopied(false), 2000); }}>
                {secretCopied ? <Check size={12} /> : <Copy size={12} />} {secretIssued}
              </button>
              <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
                Shown once. Your previous secret keeps working for a while, so you
                can change this over without losing deliveries.
              </p>
            </div>
          ) : (
            <button type="button" className="btn btn-ghost" disabled={busy}
                    data-testid="rotate-webhook-secret" onClick={rotateSecret}>
              {connector.has_webhook_secret ? "Replace signing secret" : "Generate signing secret"}
            </button>
          )}
        </div>
      )}

      {/* Every kind that reaches outside Prioris has a demo stand-in until it
          is marked live: sources that poll, and profile lookups that call a
          tenant's API. Hiding this for either leaves no way to go live. */}
      {kind.capabilities?.some((c) => c === "inbound_poll" || c === "profile_lookup") && (
        <label className="flex items-center gap-2 mt-4 text-[12.5px]"
               style={{ color: "var(--text-secondary)" }}>
          <input type="checkbox" checked={live} data-testid="field-live"
                 onChange={(e) => setLive(e.target.checked)} />
          Connect to the real source. Requires a live workspace and the entitlement.
        </label>
      )}

      {tested && (
        <div className={`badge mt-3 inline-block ${tested.ok ? "b-healthy" : "b-rev"}`}
             data-testid="test-result">
          {tested.detail}
        </div>
      )}
      {error && <div className="badge b-rev mt-3 inline-block" data-testid="connector-error">{error}</div>}

      <div className="flex gap-2 mt-4">
        <button className="btn" type="submit" disabled={busy} data-testid="save-connector">
          {busy ? "Saving…" : isEdit ? "Save changes" : "Add source"}
        </button>
        {isEdit && (
          // Only offered on a saved connector: a test needs stored credentials,
          // and asking the server about a configuration it has never seen would
          // mean sending it one — including the secret — just to try it.
          <button className="btn btn-ghost" type="button" onClick={test}
                  disabled={busy} data-testid="test-connector">
            Test connection
          </button>
        )}
        <button className="btn btn-ghost" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
